import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import { Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import TroopBox from "../../../components/Boxes/TroopBox";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialog";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHook";
import useToast from "../../../hooks/useToast";
import {
  fetchGetMe,
  fetchTroopLevels,
  trainRequestPlayerTroop,
} from "../../../redux/pages/barracks/barracksActions";
import { BarracksState } from "../../../redux/pages/barracks/barracksSlice";
import { roundFloat } from "../../../utils/round-float";
import { useBarracksUIContext } from "../BarracksContext";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

const TrainDialog = () => {
  const dispatch = useAppDispatch();
  const openToast = useToast();
  const { t } = useAppTranslation(["barracks"]);

  const [troops, setTroops] = useState([]);

  const { barrackBuildingInfo }: BarracksState = useAppSelector(
    ({ barracks }) => {
      return {
        barrackBuildingInfo: barracks.barrackBuildingInfo,
      };
    },
    shallowEqual
  ) as BarracksState;

  const barracksContext = useBarracksUIContext();
  const barracksUIProps = useMemo(() => {
    return {
      trainDialog: barracksContext?.trainDialog,
      setTrainDialog: barracksContext?.setTrainDialog,
      setConfirmDialog: barracksContext?.setConfirmDialog,
      setUpgradeLoading: barracksContext?.setUpgradeLoading,
    };
  }, [
    barracksContext?.trainDialog,
    barracksContext?.setTrainDialog,
    barracksContext?.setConfirmDialog,
    barracksContext?.setUpgradeLoading,
  ]);

  const handleTrain = async (id: number) => {
    barracksUIProps?.setUpgradeLoading?.(true);

    dispatch(trainRequestPlayerTroop({ troopId: id }))
      .then((response) => {
        barracksUIProps?.setConfirmDialog?.((prev) => ({
          ...prev,
          open: false,
        }));
        response && openToast(response);
        if (response?.ok) {
          barracksUIProps?.setTrainDialog?.(false);
        }
      })
      .finally(() => {
        barracksUIProps?.setUpgradeLoading?.(false);
      });

    dispatch(fetchGetMe());
  };
  useEffect(() => {
    dispatch(fetchTroopLevels()).then((response) => {
      setTroops(response.data);
    });
  }, []);

  return (
    <Dialog
      size="xl"
      onClose={() => barracksUIProps?.setTrainDialog?.(false)}
      open={!!barracksUIProps?.trainDialog}
      title={t("barracks:dialogs.train.title")}
    >
      <div style={{ marginBottom: -40 }}>
        <Swiper
          slidesPerView={3.5}
          modules={[Pagination, Navigation]}
          navigation
          breakpoints={{
            1200: {
              slidesPerGroup: 3,
              slidesPerView: 3.5,
            },
            1000: {
              slidesPerGroup: 2,
              slidesPerView: 2.5,
            },
            500: {
              slidesPerGroup: 1,
              slidesPerView: 1.5,
            },
            290: {
              slidesPerGroup: 1,
              slidesPerView: 1.1,
            },
          }}
        >
          {troops?.map((troop: any, index: number) => {
            let discountPercentage: boolean | string | number = false;
            if (
              troop.clegCost &&
              troop.lastClegCost &&
              troop.clegCost !== troop.lastClegCost
            ) {
              discountPercentage = roundFloat(
                100 - (troop.clegCost * 100) / troop.lastClegCost,
                0
              );
            }

            return troop.clegCost ? (
              <SwiperSlide key={troop?.id ?? index}>
                <TroopBox
                  {...troop}
                  train
                  troop={troop}
                  bonus={barrackBuildingInfo?.bonusStats}
                  abilities={troop?.abilities && JSON.parse(troop?.abilities)}
                  action={
                    <Button
                      badge={
                        discountPercentage && (
                          <>
                            <div className="dark-yellow fs-lg">
                              {discountPercentage}%
                            </div>
                            <small className="dark-yellow">
                              {t("barracks:stats.off")}
                            </small>
                          </>
                        )
                      }
                      onClick={() =>
                        barracksUIProps?.setConfirmDialog?.({
                          open: true,
                          submit: () => handleTrain(troop.id),
                          msg: t("barracks:dialogs.train.confirm", {
                            troop: troop.name,
                          }),
                          title: t("barracks:labels.areYouSure"),
                        })
                      }
                      variant="primary"
                    >
                      {t("barracks:labels.train")}
                    </Button>
                  }
                />
              </SwiperSlide>
            ) : (
              ""
            );
          })}
        </Swiper>
      </div>
    </Dialog>
  );
};

export default TrainDialog;
